import { pick } from 'lodash';
import { IS_ONE_SEALINK } from 'utils/constant';

export const onOverviewOrCheckout = () => {
  const { pathname } = window.location;
  const result = ['/bookings', '/checkout', '/cart'].find((path) => {
    const noSlashPath = pathname.replace(/\/+$/, ''); // Remove trailing slash

    return noSlashPath.endsWith(path);
  });

  return Boolean(result);
};

export const STORAGE_KEY = 'abandonedCartModal';

export function objectToFormData(obj) {
  const formData = new FormData();

  Object.entries(obj).forEach(([key, value]) => {
    formData.append(key, value);
  });

  return formData;
}

export function requestToSalesforce({ accountRelated, formHandle }) {
  const { siteId, api_prefix = '', csrf } = window.ecom.craftConfig;
  const { tokenName, tokenValue } = csrf;
  const data = {
    action: 'formie/submissions/submit',
    handle: formHandle || 'cartAbandonment',
    siteId,
    'fields[firstName]': accountRelated.firstName,
    'fields[lastName]': accountRelated.lastName,
    'fields[email]': accountRelated.email,
    'fields[recordTypeId]': '0122w000001fPRrAAM',
    [tokenName]: tokenValue,
  };

  if (accountRelated.subscribeToMarketingEmails) {
    data['fields[subscribeToMarketingEmails]'] = 1;
  }

  fetch(`${api_prefix}/`, {
    method: 'post',
    body: objectToFormData(data),
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

export const getPassenger = (id, types) => {
  const type = types.find((item) => item.id === id) || {};
  return pick(type, 'passenger_type_id');
};

export const getPassengerTypes = (ids, types) => {
  const passengerTypes = {};

  ids.forEach((id) => {
    const type = types.find((item) => item.id === id) || {};
    passengerTypes[type.passenger_type_id] = passengerTypes[
      type.passenger_type_id
    ]
      ? ++passengerTypes[type.passenger_type_id]
      : 1;
  });

  return passengerTypes;
};

export const getVehicleType = (id, types) => {
  const type = types.find((item) => item.id === id) || {};

  return {
    length: type.length,
    width: type.width,
    height: type.height,
    weight: type.weight,
    details: type.details,
    registration: type.registration,
    vehicle_type_id: type.vehicle_type_id,
    make_code: type.make_code,
    family_code: type.family_code,
    body_style_code: type.body_style_code,
    year_code: type.year_code,
    variant_code: type.variant_code,
    attachments: type.attachments.map((attachment) => ({
      code: attachment.code,
      description: attachment.description,
      length_value: attachment.length_value,
      height_value: attachment.height_value,
      width_value: attachment.width_value,
      weight_value: attachment.weight_value,
      size_unit: attachment.size_unit,
      weight_unit: attachment.weight_unit,
    })),
  };
};

export const buildPayload = ({
  emailFromForm,
  subscribe,
  booking,
  checkoutForm,
}) => {
  let payload = {};
  const rebuildData = {
    reservations: [],
    customer: {},
  };
  const { firstName, lastName, userPhoneNumber, countryId, postCode } =
    checkoutForm;
  let userName = null;
  if (firstName && lastName) {
    userName = `${firstName} ${lastName}`;
  }
  const {
    balance_in_cents: balanceInCents,
    reference,
    reservations_attributes: reservations,
    passengers_attributes: passengerTypes,
    vehicles_attributes: vehiclesTypes,
    customer_contact_name,
    customer_contact_phone,
    customer_contact_mobile,
    country_id,
    post_code,
  } = booking;

  // Booking is not paid yet.
  if (balanceInCents > 0) {
    payload = {
      booking: {
        booking_reference: reference,
        site: ecom.craftConfig.site,
        balance_in_cents: balanceInCents,
        resources: reservations.map(
          ({
            resource_id,
            gross_including_packaged_item_in_cents,
            first_travel_date,
            last_travel_date,
            passenger_ids,
          }) => ({
            resourceId: resource_id,
            price: gross_including_packaged_item_in_cents,
            travel_start_date: first_travel_date._value,
            travel_end_date: last_travel_date._value,
            passengers: passenger_ids.map((paxId) =>
              getPassenger(paxId, passengerTypes)
            ),
          })
        ),
      },
      customer: {
        customer_contact_name: userName || customer_contact_name,
        customer_contact_phone,
        customer_contact_mobile: userPhoneNumber || customer_contact_mobile,
        customer_contact_email: emailFromForm,
        country_id: countryId || country_id,
        post_code: postCode || post_code,
      },
    };

    reservations.forEach((reservation) => {
      switch (reservation.resource_class_name) {
        case 'Ship':
        case 'ScheduledTripResource':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            route_id: reservation.route_id,
            forward: {
              trip_id: reservation.trip_id,
              first_travel_date: reservation.first_travel_date._value,
              from_route_stop_id: reservation.from_route_stop_attributes.id,
              to_route_stop_id: reservation.to_route_stop_attributes.id,
              passenger_types: getPassengerTypes(
                reservation.passenger_ids,
                passengerTypes
              ),
              vehicle_types: reservation.vehicle_ids.map((vehicleId) =>
                getVehicleType(vehicleId, vehiclesTypes)
              ),
            },
            segments: ['forward'],
          });
          break;
        case 'Package':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
            sub_reservations:
              reservation.sub_reservations_attributes?.map(
                (subReservation) => ({
                  from_route_stop_id:
                    subReservation.from_route_stop_attributes?.id || null,
                  to_route_stop_id:
                    subReservation.to_route_stop_attributes?.id || null,
                  bed_configuration: subReservation.bed_configuration || null,
                })
              ) || [],
          });
          break;
        case 'Accommodation':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            last_travel_date: reservation.last_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
            bed_configuration: reservation.bed_configuration,
          });
          break;
        case 'Item':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            last_travel_date: reservation.last_travel_date._value,
            quantity: reservation.quantity,
          });
          break;
        case 'GenericResource':
          rebuildData.reservations.push({
            resource_class_name: reservation.resource_class_name,
            resource_id: reservation.resource_id,
            first_travel_date: reservation.first_travel_date._value,
            passenger_types: getPassengerTypes(
              reservation.passenger_ids,
              passengerTypes
            ),
          });
          break;
        default:
          break;
      }
    });

    rebuildData.customer = payload.customer;
    const url = IS_ONE_SEALINK
      ? `${ecom.craftConfig.siteUrl}/bookings`
      : `${window.location.origin}/cart`;
    payload.rebuild_url = `${url}/rebuild?json=${encodeURIComponent(
      JSON.stringify(rebuildData)
    )}`;
  }
  const accountRelated = {
    firstName,
    lastName: lastName || customer_contact_name || emailFromForm,
    email: emailFromForm,
    subscribeToMarketingEmails: subscribe ? 1 : 0,
  };
  return { payload, accountRelated };
};
